import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
  type MouseEvent,
  type ReactElement,
} from 'react';
import { ChevronDown, Command, ListTree, Plus, Settings as SettingsIcon, X } from 'lucide-react';
import type { PlatformBridge } from '@slate/core';
import { useDocStore } from '../store/docStore';
import { effectiveFileName } from '../util/docLanguage';
import { FileTypeIcon } from '../icons/FileTypeIcon';
import { SettingsDialog } from './SettingsDialog';
import { RestartConfirmDialog } from './RestartConfirmDialog';
import { InlineEditInput } from './InlineEditInput';
import { useContextMenu, useLongPress, type ContextMenuEntry } from '../context-menu';
import { renameNode } from '../fs/fsActions';
import { isUnderRoot, dirOf } from '../fs/containment';
import { toast } from '../store/toastStore';
import { SlateLogo } from './SlateLogo';
import { isTauriEnv } from './useTauriEnv';
import { buildLogoMenu } from './headerMenuItems';
import { HelpMenu } from './HelpMenu';
import { ShortcutBadge } from './ShortcutBadge';
import { shortcutLabel } from '../keymap/platform';

export interface TitleBarProps {
  /**
   * Reserve the macOS traffic-light inset at the LEFT of the strip. Only true
   * when the sidebar is collapsed (otherwise the sidebar header owns the inset).
   */
  reserveTrafficLights: boolean;
  /**
   * Render the outline-pin toggle in the right-hand controls strip (next to
   * Settings). When `false`, the button is hidden — used for non-markdown
   * docs / empty state where there's no outline to pin.
   */
  showOutlineToggle?: boolean;
  /** Current outline-pinned state (when the toggle is shown). */
  outlinePinned?: boolean;
  /** Handler for the outline toggle button. */
  onToggleOutline?: () => void;
  /**
   * Threaded through to the SettingsDialog so the About panel (web-only)
   * can fetch the running version via `platform.getAppVersion()`.
   */
  platform?: PlatformBridge;
}

/**
 * Draggable window title strip (Tauri drag region) holding the open-document
 * tabs. Empty background areas carry `data-tauri-drag-region`; interactive
 * children (tabs, close, add) must NOT, or their clicks would be swallowed.
 */
export function TitleBar({
  reserveTrafficLights,
  showOutlineToggle = false,
  outlinePinned = false,
  onToggleOutline,
  platform,
}: TitleBarProps): ReactElement {
  const docs = useDocStore((s) => s.docs);
  const activeId = useDocStore((s) => s.activeId);
  const activateDoc = useDocStore((s) => s.activateDoc);
  const requestCloseDocs = useDocStore((s) => s.requestCloseDocs);
  const moveDoc = useDocStore((s) => s.moveDoc);
  const newUntitled = useDocStore((s) => s.newUntitled);
  const togglePalette = useDocStore((s) => s.togglePalette);
  const openPaletteWithSeed = useDocStore((s) => s.openPaletteWithSeed);
  const workspaceRoots = useDocStore((s) => s.workspaceRoots);
  const applyPathRename = useDocStore((s) => s.applyPathRename);
  const { openContextMenu } = useContextMenu();

  /**
   * Inline tab rename. `null` when no tab is being renamed. Only file-backed
   * docs (those with `ref.path`) are renamable — untitled buffers have no
   * disk identity yet, so renaming would be ambiguous.
   */
  const [renamingTab, setRenamingTab] = useState<{ id: string; value: string } | null>(null);

  /** Pick the workspace root that contains `filePath`, or `null` if none does. */
  const findContainingRoot = useCallback(
    (filePath: string): string | null => {
      for (const root of workspaceRoots) {
        if (root.path && isUnderRoot(filePath, root.path)) return root.path;
      }
      return null;
    },
    [workspaceRoots],
  );

  const startTabRename = useCallback(
    (docId: string): void => {
      const doc = docs.find((d) => d.id === docId);
      if (!doc?.ref?.path) return; // untitled / no path → can't rename on disk
      setRenamingTab({ id: docId, value: doc.ref.name ?? doc.ref.path.split(/[\\/]/).pop() ?? '' });
    },
    [docs],
  );

  const commitTabRename = useCallback(async (): Promise<void> => {
    const current = renamingTab;
    if (!current || !platform) {
      setRenamingTab(null);
      return;
    }
    setRenamingTab(null);
    const doc = useDocStore.getState().docs.find((d) => d.id === current.id);
    const oldPath = doc?.ref?.path;
    if (!oldPath) return;
    const trimmed = current.value.trim();
    if (trimmed === '' || trimmed === doc?.ref?.name) return; // no-op
    // A tab can back a file that isn't inside any open workspace folder (opened
    // standalone via Open File or `slate <file>`). Renaming only ever changes
    // the basename within the same directory, and renameNode's validateName
    // forbids path separators, so the file's own parent directory is a safe
    // containment root — fall back to it instead of refusing the rename.
    const root = findContainingRoot(oldPath) ?? dirOf(oldPath);
    const newPath = await renameNode(
      { platform, workspaceRoot: root, toast: (m, o) => toast(m, o) },
      oldPath,
      trimmed,
    );
    if (!newPath) return;
    applyPathRename(oldPath, newPath);
  }, [renamingTab, platform, findContainingRoot, applyPathRename]);

  const cancelTabRename = useCallback((): void => {
    setRenamingTab(null);
  }, []);

  // Drag-to-reorder state. `draggingId` survives the gesture (ref); the
  // insertion preview (`dropTargetId` + `dropSide`) is React state so a
  // vertical accent line can render on the relevant edge.
  const draggingId = useRef<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [dropSide, setDropSide] = useState<'before' | 'after' | null>(null);
  /** Settings dialog visibility (Appearance category by default). */
  const [settingsOpen, setSettingsOpen] = useState(false);
  /** Restart-confirmation dialog visibility. The logo menu's Restart item opens
   *  this; confirming relaunches via the platform bridge. */
  const [restartConfirmOpen, setRestartConfirmOpen] = useState(false);

  // The `/settings` slash command (and any other code path) opens the dialog by
  // dispatching a `slate:open-settings` window event — the dialog state lives
  // here, so bridge it through a listener rather than lifting state.
  useEffect(() => {
    const onOpenSettings = (): void => setSettingsOpen(true);
    window.addEventListener('slate:open-settings', onOpenSettings);
    return () => window.removeEventListener('slate:open-settings', onOpenSettings);
  }, []);

  // The logo dropdown (the in-app replacement for the native app menu). Opens
  // just below the logo. About/Acknowledgements reuse the existing
  // `slate:open-about` window event (carrying the ack flag); Quit/Check for
  // Updates are desktop-only.
  const openLogoMenu = useCallback(
    (e: MouseEvent): void => {
      const r = e.currentTarget.getBoundingClientRect();
      openContextMenu(
        r.left,
        r.bottom + 4,
        buildLogoMenu({
          isDesktop: isTauriEnv(),
          onAbout: () =>
            window.dispatchEvent(new CustomEvent('slate:open-about', { detail: { ack: false } })),
          onAcknowledgements: () =>
            window.dispatchEvent(new CustomEvent('slate:open-about', { detail: { ack: true } })),
          onSettings: () => setSettingsOpen(true),
          onCheckUpdates: () => window.dispatchEvent(new Event('slate:check-updates')),
          onQuit: () => void platform?.quit?.(),
          // Gate on the platform capability; open the confirm dialog rather than
          // relaunching immediately so unsaved work isn't lost without a prompt.
          onRestart: platform?.restartApp ? () => setRestartConfirmOpen(true) : undefined,
        }),
      );
    },
    [openContextMenu, platform],
  );

  const onClose = (e: MouseEvent, id: string): void => {
    e.stopPropagation();
    requestCloseDocs([id]);
  };

  const onTabDragStart = useCallback((e: ReactDragEvent, id: string): void => {
    draggingId.current = id;
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const onTabDragOver = useCallback((e: ReactDragEvent, id: string): void => {
    // Only react to an in-progress tab drag (ignore file drops onto the strip).
    if (!draggingId.current) return;
    e.preventDefault(); // allow the drop
    e.dataTransfer.dropEffect = 'move';
    // Decide before-vs-after by which half of the target tab the cursor is in.
    // The preview line lands on the leading edge of the target (before) or its
    // trailing edge (after), matching where moveDoc will splice the tab in.
    const rect = e.currentTarget.getBoundingClientRect();
    const side: 'before' | 'after' = e.clientX < rect.left + rect.width / 2 ? 'before' : 'after';
    // Suppress the preview when it would be a visual no-op: dropping right
    // before the source's own slot, or right after it.
    if (id === draggingId.current) {
      setDropTargetId(null);
      setDropSide(null);
      return;
    }
    setDropTargetId((cur) => (cur === id ? cur : id));
    setDropSide((cur) => (cur === side ? cur : side));
  }, []);

  const onTabDrop = useCallback(
    (e: ReactDragEvent, targetId: string): void => {
      const dragId = draggingId.current ?? e.dataTransfer.getData('text/plain');
      const side = dropSide ?? 'before';
      if (dragId) {
        e.preventDefault();
        moveDoc(dragId, targetId, side);
      }
      draggingId.current = null;
      setDropTargetId(null);
      setDropSide(null);
    },
    [moveDoc, dropSide],
  );

  const onTabDragEnd = useCallback((): void => {
    draggingId.current = null;
    setDropTargetId(null);
    setDropSide(null);
  }, []);

  // Tab context menu: Close / Close others / Close all. Reads the latest doc
  // list at activation time so closing several stays consistent.
  const buildTabMenu = useCallback(
    (id: string): ContextMenuEntry[] => {
      const others = docs.filter((d) => d.id !== id);
      const target = docs.find((d) => d.id === id);
      const canRename = !!target?.ref?.path;
      // Language can be changed on any editable text doc (image docs have none).
      const canChangeLanguage = !!target && target.kind !== 'image';
      return [
        {
          id: 'rename',
          label: 'Rename',
          disabled: !canRename,
          onSelect: () => startTabRename(id),
        },
        {
          id: 'change-language',
          label: 'Change language',
          disabled: !canChangeLanguage,
          // Target this tab, then open the palette pre-seeded with `/lang ` so the
          // language picker applies to it. (The palette's `/lang` run reads the
          // active doc id, which is why we activate first.)
          onSelect: () => {
            activateDoc(id);
            openPaletteWithSeed('/lang ');
          },
        },
        { id: 'close', label: 'Close', onSelect: () => requestCloseDocs([id]) },
        {
          id: 'close-others',
          label: 'Close others',
          disabled: others.length === 0,
          onSelect: () =>
            requestCloseDocs(
              useDocStore
                .getState()
                .docs.filter((d) => d.id !== id)
                .map((d) => d.id),
            ),
        },
        {
          id: 'close-all',
          label: 'Close all',
          disabled: docs.length === 0,
          onSelect: () =>
            requestCloseDocs(useDocStore.getState().docs.map((d) => d.id)),
        },
      ];
    },
    [docs, requestCloseDocs, startTabRename, activateDoc, openPaletteWithSeed],
  );

  const onTabContextMenu = useCallback(
    (e: MouseEvent, id: string): void => {
      e.preventDefault();
      e.stopPropagation();
      openContextMenu(e.clientX, e.clientY, buildTabMenu(id));
    },
    [openContextMenu, buildTabMenu],
  );

  // Touch long-press → same menu. Resolve the tab under the touch point so one
  // hook serves every tab (rules-of-hooks safe).
  const longPress = useLongPress((x, y) => {
    const el = document.elementFromPoint?.(x, y);
    const tab = el?.closest?.('[data-tab-id]') as HTMLElement | null;
    const id = tab?.dataset.tabId;
    if (id) openContextMenu(x, y, buildTabMenu(id));
  });

  return (
    <div
      className="mp-titlebar"
      data-tauri-drag-region
      data-reserve-traffic-lights={reserveTrafficLights ? '' : undefined}
    >
      {reserveTrafficLights && (
        <span className="mp-titlebar__traffic-inset" data-tauri-drag-region aria-hidden />
      )}

      <button
        type="button"
        className="mp-header__logo"
        aria-haspopup="menu"
        aria-label="Slate menu"
        title="Slate menu"
        onClick={openLogoMenu}
      >
        <SlateLogo className="mp-header__logo-icon" />
        <span className="mp-header__logo-name">Slate</span>
        <ChevronDown size={12} aria-hidden />
      </button>

      <div
        className="mp-tabs"
        role="tablist"
        aria-label="Open documents"
        onTouchStart={longPress.onTouchStart}
        onTouchMove={longPress.onTouchMove}
        onTouchEnd={longPress.onTouchEnd}
        onTouchCancel={longPress.onTouchCancel}
      >
        {docs.map((doc) => {
          const name = doc.ref?.name ?? 'Untitled';
          // The tab ICON follows the effective language so a `/lang` override
          // shows that language's icon (e.g. a TypeScript icon on an untitled
          // buffer). The tab TEXT stays the real name.
          const iconName = doc.kind === 'image' ? name : effectiveFileName(doc) ?? name;
          const active = doc.id === activeId;
          const isRenaming = renamingTab?.id === doc.id;
          const canRename = !!doc.ref?.path;
          return (
            <div
              key={doc.id}
              role="tab"
              tabIndex={0}
              aria-selected={active}
              // Only the ACTIVE tab carries the close shortcut (Cmd/Ctrl+W
              // closes whatever tab is active — see App's global keydown).
              aria-keyshortcuts={active ? shortcutLabel('w') : undefined}
              className="mp-tab"
              draggable={!isRenaming}
              data-active={active ? '' : undefined}
              data-drop-target={dropTargetId === doc.id ? '' : undefined}
              data-drop-side={dropTargetId === doc.id ? dropSide ?? undefined : undefined}
              data-tab-id={doc.id}
              title={doc.ref?.path ?? name}
              onClick={() => activateDoc(doc.id)}
              onDoubleClick={(e) => {
                if (!canRename) return;
                e.preventDefault();
                e.stopPropagation();
                startTabRename(doc.id);
              }}
              onContextMenu={(e) => onTabContextMenu(e, doc.id)}
              onDragStart={(e) => onTabDragStart(e, doc.id)}
              onDragOver={(e) => onTabDragOver(e, doc.id)}
              onDrop={(e) => onTabDrop(e, doc.id)}
              onDragEnd={onTabDragEnd}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  activateDoc(doc.id);
                }
              }}
            >
              <FileTypeIcon name={iconName} kind="file" size={14} className="mp-tab__icon" />
              {isRenaming ? (
                <InlineEditInput
                  value={renamingTab.value}
                  onChange={(v) =>
                    setRenamingTab((s) => (s ? { ...s, value: v } : s))
                  }
                  onCommit={() => void commitTabRename()}
                  onCancel={cancelTabRename}
                  ariaLabel={`Rename ${name}`}
                  className="mp-doc-tab__rename"
                />
              ) : (
                <span className="mp-tab__name">{name}</span>
              )}
              {doc.dirty && <span className="mp-tab__dirty" aria-label="Unsaved changes" />}
              <button
                type="button"
                className="mp-tab__close"
                aria-label={`Close ${name}`}
                title="Close"
                onClick={(e) => onClose(e, doc.id)}
              >
                <X size={13} aria-hidden strokeWidth={2.25} />
              </button>
              {/* Cmd/Ctrl+W closes the active tab. Anchored top-LEFT so it
                  doesn't collide with the always-visible close (×) button that
                  occupies the active tab's top-right corner. */}
              {active && (
                <ShortcutBadge label={shortcutLabel('w')} className="mp-kbd-badge--tab" />
              )}
            </div>
          );
        })}

        <button
          type="button"
          className="mp-tab__add"
          aria-label="New document"
          aria-keyshortcuts={shortcutLabel('n')}
          title={`New document (${shortcutLabel('n')})`}
          onClick={() => newUntitled()}
        >
          <Plus size={15} aria-hidden strokeWidth={2.25} />
          <ShortcutBadge label={shortcutLabel('n')} />
        </button>
      </div>

      {/* Spacer fills the rest of the strip and stays draggable. */}
      <div className="mp-titlebar__spacer" data-tauri-drag-region />

      {/* Right-hand controls (NOT a drag region — must receive clicks).
          Settings comes first; the outline-pin toggle sits immediately to its
          right when the active doc has an outline (markdown). */}
      <div className="mp-titlebar__controls">
        <button
          type="button"
          className="mp-iconbtn"
          aria-label="Open command palette"
          aria-keyshortcuts={shortcutLabel('k')}
          title={`Open command palette (${shortcutLabel('k')})`}
          onClick={togglePalette}
        >
          <Command size={15} aria-hidden />
          <ShortcutBadge label={shortcutLabel('k')} />
        </button>
        <button
          type="button"
          className="mp-iconbtn"
          aria-label="Settings"
          aria-keyshortcuts={shortcutLabel(',')}
          title={`Settings (${shortcutLabel(',')})`}
          onClick={() => setSettingsOpen(true)}
        >
          <SettingsIcon size={15} aria-hidden />
          <ShortcutBadge label={shortcutLabel(',')} />
        </button>
        {platform && <HelpMenu platform={platform} />}
        {showOutlineToggle && onToggleOutline && (
          <button
            type="button"
            className="mp-iconbtn"
            aria-pressed={outlinePinned}
            aria-label="Toggle outline"
            title="Toggle outline panel"
            onClick={onToggleOutline}
          >
            <ListTree size={15} aria-hidden />
          </button>
        )}
      </div>

      <SettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        platform={platform}
      />

      <RestartConfirmDialog
        open={restartConfirmOpen}
        onCancel={() => setRestartConfirmOpen(false)}
        onConfirm={() => {
          setRestartConfirmOpen(false);
          void (async () => {
            try {
              await platform?.restartApp?.();
            } catch (err) {
              // Under `tauri dev` the relaunch can't happen; in production it
              // does. Either way, never swallow the failure silently (which made
              // the menu item look like it "does nothing").
              const detail = err instanceof Error ? err.message : String(err);
              toast(`Could not restart Slate: ${detail}`, { kind: 'error', sticky: true });
            }
          })();
        }}
      />
    </div>
  );
}
